import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Community Fund nomination state for a single work, scoped to the open
 * voting period.
 *
 * Returns:
 *   nominationCount — nominations for this work in the open period
 *   hasNominated    — whether the current user already nominated this work
 *   isLoading       — initial fetch in flight
 *   nominate()      — calls the nominate_work_for_pot RPC; resolves with the
 *                     RPC payload, throws Error with a user-readable message
 */
export function usePotNomination(workId) {
  const { user } = useAuth();
  const [periodId, setPeriodId] = useState(null);
  const [nominationCount, setNominationCount] = useState(0);
  const [hasNominated, setHasNominated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!workId) return;
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const { data: period } = await supabase
          .from('pot_voting_periods')
          .select('id')
          .eq('status', 'open')
          .order('starts_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (cancelled) return;
        if (!period) {
          setPeriodId(null);
          setNominationCount(0);
          setHasNominated(false);
          return;
        }
        setPeriodId(period.id);

        const countQuery = supabase
          .from('pot_nominations')
          .select('id', { count: 'exact', head: true })
          .eq('period_id', period.id)
          .eq('work_id', workId);

        const mineQuery = user
          ? supabase
              .from('pot_nominations')
              .select('id')
              .eq('period_id', period.id)
              .eq('work_id', workId)
              .eq('user_id', user.id)
              .maybeSingle()
          : Promise.resolve({ data: null });

        const [countRes, mineRes] = await Promise.all([countQuery, mineQuery]);
        if (cancelled) return;
        setNominationCount(countRes.count ?? 0);
        setHasNominated(!!mineRes.data);
      } catch (err) {
        console.error('Failed to fetch pot nomination state:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [workId, user]);

  const nominate = useCallback(async () => {
    const { data, error } = await supabase.rpc('nominate_work_for_pot', {
      p_work_id: workId,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Nomination failed');
    setHasNominated(true);
    setNominationCount((c) => c + 1);
    return data;
  }, [workId]);

  return { periodId, nominationCount, hasNominated, isLoading, nominate };
}
